import { call, put, takeLatest, select } from 'redux-saga/effects';
import { apiGetCall, apiPostCall, apiPutCall } from '../api';
import {
  fetchQuestionnaireRequest, fetchQuestionnaireError,
  fetchQuestionnaireSuccess, setSurveyDetails,
  submitQuestionnaireError, submitQuestionnaireRequest,
  submitQuestionnaireSuccess,
  QuestionnaireWithAnswerFailure,
  QuestionnaireWithAnswerStart,
  QuestionnaireWithAnswerSuccess,
  SpQAPIsubmitQuestionnaireFailure,
  SpQAPIsubmitQuestionnaireStart,
  SpQAPIsubmitQuestionnaireSuccess,
  setSubmitSuccess
} from '../slices/questionnaireSlice';
import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { PayloadAction } from '@reduxjs/toolkit';
import { getStatus,setApiStatus,setShowSuccessPopUp } from '../slices/sellerProfileSlice';
import { showAPIerror } from '../../pages/SellerProfile/APIs/Helpers';

const URL = process.env.REACT_APP_API_BASE;

function* checkTokenExpired() {
  // if (jwtDecode(token).exp < Date.now() / 1000) {
  yield (window.instance as any)
    .acquireTokenSilent({
      redirectUri: process.env.REACT_APP_AZURE_REDIRECT,
      authority: process.env.REACT_APP_AZURE_AUTHORITY
    })
    .then((res) => {
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.idToken}`;
    })
    .catch((e) => {
      if (e instanceof InteractionRequiredAuthError) {
        (window.instance as any).loginRedirect();
      }
    });
}

function* fetchQuestionnaireSaga(action) {
  try {
    yield call(checkTokenExpired);
    const response = yield call(apiGetCall, URL + 'survey-details');
    const questions = response.questions;
    const language = action.payload.language;
    yield put(fetchQuestionnaireSuccess({ questions, language }));
    yield put(setSurveyDetails(questions));
  } catch (error: any) {
    yield put(fetchQuestionnaireError(error.message));
  }
}

function* submitQuestionnaireSaga(action: PayloadAction<any>) {
  try {
    yield call(checkTokenExpired);
    const { formUpdates, language } = action.payload;
    const { user_id, seller_id } = yield select((state) => state.sellerProfile);
    const surveyDetails = yield select((state) => state.questionnaire.surveyDetails);
    const formattedDataParent = { survey_data: {} };
    const formattedData = formattedDataParent.survey_data;
    const answers = {};
    const sub_question = language === 'fr' ? 'sub_question_fr' : 'sub_question_en';

    const formatUpdatesForSubmission = (field, updates) => {
      Object.keys(updates).forEach((key) => {
        if (key === `${field}-SUB`) {
          // Get answers for subquestions and deal with unique formatting for submission
          const subQs = updates[key]['options'];
          const subQsKeys = Object.keys(subQs);
          subQsKeys.forEach((subkey) => {
            answers[subkey] = updates[key]['options'][subkey].content;
          });
        } else if (key === field) {
          if (updates[key].type === 'radio') {
            // Get radio/boolean answer
            Object.keys(updates[key].options).forEach((option) => {
              if (updates[key]['options'][option]['checked'] === true) {
                answers[field] = updates[key]['options'][option]['content'];
              }
            });
          } else if (updates[key].options) {
            // Get selected options for checkbox answers
            const contentArray: string[] = [];
            Object.keys(updates[key].options).forEach((option) => {
              if (updates[key]['options'][option]['checked'] === true) {
                // Add ID of the selected option as answer
                contentArray.push(
                  updates[key]['options'][option]['id'].toString()
                );
              }
            });
            answers[field] = contentArray;
          } else {
            // Get standard (text) answers and format
            answers[field] = updates[key].content || updates[key].checked;
          }
        } else if (typeof updates[key] === 'object' && updates[key] !== null) {
          // Wrong key, try next...
          formatUpdatesForSubmission(field, updates[key]);
        }
      });
    };

    Object.keys(surveyDetails).forEach((key) => {
      if (surveyDetails[key]['survey_question_id'].includes('QUES')) {
        formatUpdatesForSubmission(
          surveyDetails[key]['survey_question_id'],
          formUpdates['questionnaire'] ||
          formUpdates['updateQuestionnaire']
        );

        // Parse the API data about the sub questions...
        if (surveyDetails[key][sub_question] !== null) {
          const subQs = surveyDetails[key][sub_question].split('//');

          !formattedData.hasOwnProperty(
            surveyDetails[key]['survey_question_id']
          ) &&
            (formattedData[surveyDetails[key]['survey_question_id']] = {
              sub_question: {}
            });
          const currSubQObj =
            formattedData[surveyDetails[key]['survey_question_id']][
            'sub_question'
            ];

          subQs.forEach((subq) => {
            const subFields = subq.split('|');
            const subQcontent: any = {};
            let subQID = '';
            subFields.forEach((field) => {
              const finalContent = field.split(':');
              if (finalContent[0] === 'question_type_id') {
                subQcontent[finalContent[0]] = finalContent[1];
              } else if (finalContent[0] === 'optional') {
                subQcontent[finalContent[0]] =
                  finalContent[1] === '1' ? true : false;
              } else if (finalContent[0] === 'survey_sub_question_id') {
                subQID = finalContent[1];
              }
            });

            subQcontent['answer'] = answers[subQID] || '';
            currSubQObj[subQID] = subQcontent;
          });
        }

        // Add standard answer and required question params, so can submit to API
        !formattedData.hasOwnProperty(
          surveyDetails[key]['survey_question_id']
        ) &&
          (formattedData[surveyDetails[key]['survey_question_id']] = {});
        const currQ =
          formattedData[surveyDetails[key]['survey_question_id']];
        currQ['question_type_id'] =
          surveyDetails[key]['question_type_id'];

        if (surveyDetails[key]['question_type_id'] === 'QT005') {
          // Radio/boolean answer has different key + capitalize first letter...
          const answer =
            answers[surveyDetails[key]['survey_question_id']];
          currQ['bool_answer'] = answer[0].toUpperCase() + answer.slice(1);
        } else if (surveyDetails[key]['question_type_id'] === 'QT003') {
          // Grouped checkbox, return ID array as 'options'
          currQ['options'] =
            answers[surveyDetails[key]['survey_question_id']];
        } else {
          currQ['answer'] =
            answers[surveyDetails[key]['survey_question_id']] || '';
        }
        currQ['optional'] =
          surveyDetails[key]['optional'] === 1 ? true : false;
      }
    });
    const response = yield call(apiPostCall, URL + 'survey/' + user_id, formattedDataParent);
    if (response.status === 'successful') {
      yield put(getStatus({ seller_id }));
      yield put(submitQuestionnaireSuccess(response));
    }
  } catch (error: any) {
    yield put(submitQuestionnaireError(error.message));
  }
}

function* SpAPIgetQuestionnaireWithAnswerSaga(action) {
  try {
    yield call(checkTokenExpired);
    const { userId, language } = action.payload;
    const { questionnaireForm } = yield select((state) => state.questionnaire);
    const response = yield call(apiGetCall, URL + 'survey/' + userId);
    const questions = response.data;
    yield put(QuestionnaireWithAnswerSuccess({ questions, language }));
    yield put(setSurveyDetails(questions));
    if (questions.length) {
      return questionnaireForm || '';
    }
    else {
      const res = yield put(fetchQuestionnaireRequest({ language }));
      try {
        res[`postQuestionnaire`] = true;
        yield put(fetchQuestionnaireSuccess(res))
        return res
      } catch (error) {
        const {spPageText} = yield select(({ sellerProfile: { spPageText } }) => ({ spPageText }));
        yield put(fetchQuestionnaireError(showAPIerror(res, spPageText,setShowSuccessPopUp,setApiStatus, 'SpAPIgetQuestionnaireIn')))
      }
    }
  }
  catch (error: any) {
    const {spPageText} = yield select(({ sellerProfile: { spPageText } }) => ({ spPageText }));
    yield put(QuestionnaireWithAnswerFailure(showAPIerror(error, spPageText,setShowSuccessPopUp,setApiStatus, 'SpAPIgetQuestionnaireWithAnswer')))
  }
}



function* SpQAPIsubmitQuestionnaireSaga(action) {
  try {
    yield call(checkTokenExpired);
    const { formUpdates, language } = action.payload;
    const { user_id, seller_id } = yield select((state) => state.sellerProfile);
    const surveyDetails = yield select((state) => state.questionnaire.surveyDetails);
    const formattedDataParent = { survey_data: {} };
    const formattedData = formattedDataParent.survey_data;
    const answers = {};
    const sub_question = language === 'fr' ? 'sub_question_fr' : 'sub_question_en';


    const formatUpdatesForSubmission = (field, updates) => {
      Object.keys(updates).forEach((key) => {
        if (key === `${field}-SUB`) {
          //get answers for subquestions and deal w/ unique formatting for submission
          const subQs = updates[key]['options'];
          const subQsKeys = Object.keys(subQs);
          subQsKeys.forEach((subkey) => {
            answers[subkey] = updates[key]['options'][subkey].content;
          });
        } else if (key === field) {
          if (updates[key].type === 'radio') {
            //get radio/boolean answer
            Object.keys(updates[key].options).forEach((option) => {
              if (updates[key]['options'][option]['checked'] === true) {
                answers[field] = updates[key]['options'][option]['name'];
              }
            });
          } else if (updates[key].options) {
            //get selected options for checkbox answers
            const contentArray: string[] = [];
            Object.keys(updates[key].options).forEach((option) => {
              if (updates[key]['options'][option]['checked'] === true) {
                //add ID of the selected option as answer
                contentArray.push(
                  updates[key]['options'][option]['id'].toString()
                );
              }
            });
            answers[field] = contentArray;
          } else {
            //get standard (text) answers and format
            answers[field] = updates[key].content || updates[key].checked;
          }
        } else if (typeof updates[key] === 'object' && updates[key] !== null) {
          //wrong key, try next...        
          formatUpdatesForSubmission(field, updates[key]);
        }
      });
    };


    surveyDetails.map((key) => {
      if (key['survey_question_id'].includes('QUES')) {
        formatUpdatesForSubmission(
          key['survey_question_id'],
          formUpdates['updateQuestionnaire']
        );

        //parse the api data about the sub questions...
        if (key[sub_question] !== null) {
          const subQs = key[sub_question].split('//');

          !formattedData.hasOwnProperty(key['survey_question_id']) &&
            (formattedData[key['survey_question_id']] = {
              sub_question: {}
            });
          const currSubQObj =
            formattedData[key['survey_question_id']]['sub_question'];

          subQs.forEach((subq) => {
            const subFields = subq.split('|');
            const subQcontent: any = {};
            let subQID = '';
            subFields.forEach((field) => {
              const finalContent = field.split(':');
              if (finalContent[0] === 'question_type_id') {
                subQcontent[finalContent[0]] = finalContent[1];
              } else if (finalContent[0] === 'optional') {
                subQcontent[finalContent[0]] =
                  finalContent[1] === '1' ? true : false;
              } else if (finalContent[0] === 'survey_sub_question_id') {
                subQID = finalContent[1];
              }
            });

            subQcontent['answer'] = answers[subQID] || '';
            subQcontent['question_type_id'] = 'QT001';
            subQcontent['optional'] = true;
            currSubQObj[subQID] = subQcontent;
          });
        }

        //add standard answer and required question params, so can submit to api

        !formattedData.hasOwnProperty(key['survey_question_id']) &&
          (formattedData[key['survey_question_id']] = {});
        const currQ = formattedData[key['survey_question_id']];
        currQ['question_type_id'] = key['question_type_id'];

        if (key['question_type_id'] === 'QT005') {
          //radio/boolean answer has different key + capitalize first letter...
          const answer = answers[key['survey_question_id']];
          currQ['bool_answer'] = answer[0].toUpperCase() + answer.slice(1);
        } else if (key['question_type_id'] === 'QT003') {
          //grouped checknbox, return ID array as 'options'
          currQ['options'] = answers[key['survey_question_id']];
        } else {
          currQ['answer'] = answers[key['survey_question_id']] || '';
        }

        if (
          key['survey_question_id'] === 'QUES001' ||
          key['survey_question_id'] === 'QUES002' ||
          key['survey_question_id'] === 'QUES008'
        ) {
          currQ['optional'] = true;
        } else {
          currQ['optional'] = false;
        }
      }

      return null;
    });

    const response = yield call(apiPutCall, URL + 'survey/' + user_id, formattedDataParent);
    if (response.status === 'Successful') {
      yield put(getStatus({ seller_id }));
      yield put(SpQAPIsubmitQuestionnaireSuccess(response));
      yield put(setSubmitSuccess(true))
    }
  } catch (error: any) {
    yield put(SpQAPIsubmitQuestionnaireFailure(error.message))

  }
}

export default function* questionnaireSaga() {
  yield takeLatest(fetchQuestionnaireRequest.type, fetchQuestionnaireSaga);
  yield takeLatest(submitQuestionnaireRequest.type, submitQuestionnaireSaga);
  yield takeLatest(QuestionnaireWithAnswerStart.type, SpAPIgetQuestionnaireWithAnswerSaga);
  yield takeLatest(SpQAPIsubmitQuestionnaireStart.type, SpQAPIsubmitQuestionnaireSaga);
}